import { format } from "date-fns";
import DATE_FORMAT from "../config/date-config";
import React from "react";

export const addIdForDataGrid = (data) => {
  // Ensure data is an array before calling map
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map((item, index) => {
    if (item.id) {
      return item;
    }
    return { id: index, ...item };
  });
};

export const removeIdForDataGrid = (data) => {
  // Ensure data is an array before calling map
  if (!data || !Array.isArray(data)) {
    return [];
  }

  return data.map((item) => {
    const { id, ...rest } = item;
    return rest;
  });
};

export const DG_FORMATTERS = {
  DATETIME: (value) => {
    if (!value) return value;
    try {
      return format(new Date(value), DATE_FORMAT.DATE_MM_DD_YYYY_WITH_TIME_S);
    } catch {
      return value;
    }
  },
  DATETIME_A: (value) => {
    if (!value) return value;
    try {
      return format(
        new Date(value),
        DATE_FORMAT.DATE_MM_DD_YYYY_WITH_TIME_M_AM
      );
    } catch {
      return value;
    }
  },
  DATE: (value) => {
    if (!value) return value;
    try {
      return format(new Date(value), DATE_FORMAT.DATE_MM_DD_YYYY);
    } catch {
      return value;
    }
  },
  TIME: (value) => {
    if (!value) return value;
    try {
      return format(new Date(value), DATE_FORMAT.TIME_M_AM);
    } catch {
      return value;
    }
  },
  NUMBER: (value) => {
    if (value === null || value === undefined) return value;
    // Format whole numbers with commas for thousands separator
    return Number(value).toLocaleString();
  },
  NUMBER_4DEC: (value) =>
    value !== null && value !== undefined ? Number(value).toFixed(4) : value,
  NUMBER_6DEC: (value) =>
    value !== null && value !== undefined ? Number(value).toFixed(6) : value,
  NUMBER_7DEC: (value) =>
    value !== null && value !== undefined ? Number(value).toFixed(7) : value,
};

export const FN_DG_KEY_DOWN = (e) => {
  if (e.key === "Tab") {
    e.preventDefault();

    // Get all editable cells BEFORE any blur (so we can find current position)
    const editableCells = document.querySelectorAll(".editable-cell");
    if (!editableCells.length) return;

    // Find the currently focused input element (if any)
    const currentInput = document.querySelector("input:focus");
    
    // Find current cell index BEFORE blur (while input is still focused)
    const currentCellIndex = Array.from(editableCells).findIndex(
      (cell) =>
        cell === document.activeElement ||
        cell.contains(document.activeElement)
    );

    if (currentInput) {
      // Trigger blur to commit the current value before moving
      currentInput.blur();

      // Small delay to allow the blur event to process
      setTimeout(() => {
        // Use the cell index we found BEFORE blur
        if (currentCellIndex >= 0) {
          const nextIndex = e.shiftKey
            ? (currentCellIndex - 1 + editableCells.length) %
            editableCells.length
            : (currentCellIndex + 1) % editableCells.length;

          // Focus the next cell
          const nextCell = editableCells[nextIndex];
          if (nextCell) {
            nextCell.focus();
            // Find the inner clickable element (div with cursor:pointer) or use the cell itself
            const clickTarget = nextCell.querySelector('div[style*="cursor: pointer"], div[style*="cursor:pointer"]') || nextCell;
            // Simulate click to start editing (for single-click edit cells like RackPricing)
            clickTarget.dispatchEvent(
              new MouseEvent("click", {
                bubbles: true,
                cancelable: true,
                view: window,
              })
            );
            // Also simulate double-click for cells that use double-click to edit
            nextCell.dispatchEvent(
              new MouseEvent("dblclick", {
                bubbles: true,
                cancelable: true,
                view: window,
              })
            );
          }
        }
      }, 10); // Small delay to ensure blur is processed
    } else {
      // If no input is focused, proceed with normal cell navigation using already-found cells and index
      if (currentCellIndex >= 0) {
        const nextIndex = e.shiftKey
          ? (currentCellIndex - 1 + editableCells.length) % editableCells.length
          : (currentCellIndex + 1) % editableCells.length;

        // Focus the next cell
        const nextCell = editableCells[nextIndex];
        if (nextCell) {
          nextCell.focus();
          // Find the inner clickable element (div with cursor:pointer) or use the cell itself
          const clickTarget = nextCell.querySelector('div[style*="cursor: pointer"], div[style*="cursor:pointer"]') || nextCell;
          // Simulate click to start editing (for single-click edit cells like RackPricing)
          clickTarget.dispatchEvent(
            new MouseEvent("click", {
              bubbles: true,
              cancelable: true,
              view: window,
            })
          );
          // Also simulate double-click for cells that use double-click to edit
          nextCell.dispatchEvent(
            new MouseEvent("dblclick", {
              bubbles: true,
              cancelable: true,
              view: window,
            })
          );
        }
      }
    }
  }
};

export const applyColumnWidths = (columns, columnWidths) => {
  return columns.map((col) => {
    // Don't apply saved widths to non-resizable columns
    if (col.resizable === false) {
      return col;
    }

    const savedWidth = columnWidths[col.field];

    // Only apply width if we have a saved preference
    // Otherwise, keep the original column definition (which may have flex)
    if (savedWidth) {
      return {
        ...col,
        width: savedWidth,
        flex: undefined, // Remove flex when using fixed width
      };
    }

    return col; // Keep original column definition with flex
  });
};

export const getDatagridFormattedValue = (col, row) => {
  if (!col || !row) return "";

  // 1. If column has valueFormatter, use it
  if (col.valueFormatter) {
    return col.valueFormatter(row[col.field], row) ?? "";
  }

  // 2. If column has renderCell, we can call it manually for search
  if (col.renderCell) {
    const rendered = col.renderCell({ row, value: row[col.field] });

    // If it returns JSX, grab its text content (strip tags)
    if (typeof rendered === "string") return rendered;
    if (typeof rendered === "number") return rendered.toString();
    if (React.isValidElement(rendered)) {
      return (rendered.props?.children ?? "").toString();
    }
    return "";
  }

  // 3. Default: raw value
  const value = row[col.field];
  return value != null ? value.toString() : "";
};

// Helper function to set hideable property for columns
export const disableHideableColumns = (columns, hideableFields = []) => {
  return columns.map((column) => ({
    ...column,
    hideable: !hideableFields.includes(column.field),
  }));
};

export const parseNumericInput = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }

  const numericRegex = /^-?\d*\.?\d*$/;

  if (!numericRegex.test(value)) {
    return null;
  }

  const result = Number(value);
  
  // Guard against NaN and non-finite values (e.g., "-", ".", "-.")
  if (!Number.isFinite(result)) {
    return null;
  }

  return result;
};
